type ProgressCallback = (progress: number) => void;

interface PendingJob {
  resolve: (data: unknown) => void;
  reject: (error: Error) => void;
  onProgress?: ProgressCallback;
}

let worker: Worker | null = null;
const pending = new Map<string, PendingJob>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./ffmpeg.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e) => {
      const msg = e.data;
      const job = pending.get(msg.id);
      if (!job) return;

      if (msg.type === 'progress') {
        job.onProgress?.(msg.progress);
        return;
      }

      pending.delete(msg.id);
      if (msg.type === 'error') {
        job.reject(new Error(msg.error));
      } else {
        job.resolve(msg);
      }
    };
  }
  return worker;
}

export interface ProxyResult {
  proxyData: ArrayBuffer;
  durationMs: number;
  width: number;
  height: number;
  fps: number;
}

export function generateProxy(
  id: string,
  fileData: ArrayBuffer,
  filename: string,
  onProgress?: ProgressCallback,
): Promise<ProxyResult> {
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve: resolve as (d: unknown) => void, reject, onProgress });
    getWorker().postMessage({ type: 'generate-proxy', id, fileData, filename }, [fileData]);
  });
}

export interface WaveformResult {
  peaks: Float32Array;
}

export function generateWaveform(
  id: string,
  fileData: ArrayBuffer,
  filename: string,
): Promise<WaveformResult> {
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve: resolve as (d: unknown) => void, reject });
    getWorker().postMessage({ type: 'generate-waveform', id, fileData, filename }, [fileData]);
  });
}

export interface ExportSegment {
  fileData: ArrayBuffer;
  sourceOffsetMs: number;
  durationMs: number;
  hasAudio: boolean;
}

export interface ExportResult {
  outputData: ArrayBuffer;
}

export interface ExportQualityConfig {
  maxWidth: number | null;
  maxHeight: number | null;
  crf: number;
  preset: string;
  audioBitrate: string;
}

export function exportTimeline(
  id: string,
  segments: ExportSegment[],
  onProgress?: ProgressCallback,
  audioMixData?: ArrayBuffer,
  quality?: ExportQualityConfig,
): Promise<ExportResult> {
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve: resolve as (d: unknown) => void, reject, onProgress });
    // Transfer all segment file data buffers
    const transfers = segments.map((s) => s.fileData);
    if (audioMixData) transfers.push(audioMixData);
    getWorker().postMessage({ type: 'export', id, segments, audioMixData, quality }, transfers);
  });
}

export function terminateWorker() {
  worker?.terminate();
  worker = null;
  pending.clear();
}
